/**
 * The Atlas's focus camera and what it reads: the bodies it can focus on, the
 * ship's own centre, the opening heliocentric fit and the pose it last left,
 * and the altitude, band and rung read off the camera.
 */

import type * as THREE from 'three';

import type { AtlasLive, Tier } from '../atlas';
import { simApi } from '../sim/api.ts';
import type { WorldVec } from '../world/frame.ts';
import type { BodyRow } from '../world/types';
import type { BodyReadersHandle } from './bodyReaders.ts';
import type { BodySceneHandle } from './bodyScene.ts';
import {
  bandAt, bandEdges, createFocusCamera,
  type Band, type FocusBodies, type FocusCameraHandle, type FramingScope,
} from './camera.ts';
import type { FocusGroundHandle } from './focusGround.ts';
import type { InjectsHandle } from './injects.ts';
import { latLonToVec3 } from './latLonToVec3.ts';
import type { MountHandle } from './mount.ts';
import { eclipticToScene } from './orrery.ts';
import type { PyramidViewHandle } from './pyramidView.ts';
import { SHIP_MARKER_ID, SHIP_MARKER_RADIUS_M } from './shipMarker.ts';
import type { SiteRegistryState } from './siteRegistry.ts';
import type { SurfaceRadiusHandle } from './surfaceRadius.ts';
import { TEST_SWITCHES } from '../urlFlags.ts';

export interface FocusRigDeps {
  readonly ORIGIN: WorldVec;
  readonly _v: THREE.Vector3;
  own(fn: () => void): void;
  zoomFloorOff(): boolean;
  readonly mount: MountHandle;
  readonly bodyReaders: BodyReadersHandle;
  readonly surfaceRadius: SurfaceRadiusHandle;
  readonly live: AtlasLive;
  readonly siteRegistry: SiteRegistryState;
  readonly bodyScene: BodySceneHandle;
  readonly focusGround: FocusGroundHandle;
  readonly injects: InjectsHandle;
  plotCamera(): boolean;
  readonly pyramidView: PyramidViewHandle;
}

/** The rig's own state that the probe reads or writes. */
export interface FocusRigState {
  openingFitReachAu: number | null;
}

export interface FocusRigHandle {
  readonly STANDING_EYE_M: 1.7;
  readonly OPENING_FIT_MAX_A_AU: 32;
  inOpeningFit(row: BodyRow): boolean;
  readonly focusBodies: FocusBodies;
  readonly focusCam: FocusCameraHandle;
  fitOpening(aspectRatio: number, scope?: FramingScope): void;
  openingFitHeld(): boolean;
  shipFocused(): boolean;
  altitude(): number;
  band(): Band;
  tierOfAltitude(): Tier;
  readonly state: FocusRigState;
}

/**
 * How far the camera may pitch up inside a cloud-top corridor, radians.
 *
 * 6.9 degrees over the anchor's horizontal. The horizon on a giant dips 3.9
 * degrees at 162 km, so this puts the limb in the lower half of a 45 degree
 * frame with the three strata stacked above it - canon 5.4's side view. Nothing
 * clamps it from below: this class is anchored at the body's CENTRE, where
 * `clearEye` has no anchor radius to derive a pitch floor from, so the eye's
 * height above the deck is the altitude itself at every pitch.
 */
const CORRIDOR_PITCH_CEILING = 0.12;

/**
 * How far above the horizon the corridor view aims, radians.
 *
 * 7.4 degrees, which on a 45 degree frame puts the limb a third of the way up
 * from the bottom edge. The horizon's own dip is subtracted, so the framing is
 * the same at the deck (2.4 degrees of dip) and at the corridor's top (5.8).
 */
const CORRIDOR_HORIZON_MARGIN = 0.13;

export function createFocusRig(deps: FocusRigDeps): FocusRigHandle {
  const { ORIGIN, _v, own, zoomFloorOff, plotCamera } = deps;
  const { camera } = deps.mount;
  const { corridorFor, descendable, descentFloorM, ladderBody, registryDatumM, rowById, smoothGround } = deps.bodyReaders;
  const { datumRadiusM, surfaceWorld } = deps.surfaceRadius;

  /**
   * A person's eye height over the drawn ground, metres.
   *
   * The same 1.7 m `camera.ts`'s `minAltitude` clamps a descent at, read here
   * as a height rather than as a distance to the focus point. Mars's own
   * walkaround floors its eye at `SURFACE_EYE_FLOOR_M`, which is a mast on a
   * vehicle and not a person.
   */
  const STANDING_EYE_M = 1.7;

  /**
   * How far the opening heliocentric view reaches, AU of semi-major axis.
   *
   * Between Neptune's 30.07 and Pluto's 39.48. A fit that reaches Pluto is
   * thirteen times Mars's orbit and puts the whole inner system inside Sol's
   * own pick disc; the body manifest panel is how a row outside the fit is
   * reached, so the fit does not have to hold every row to be usable.
   */
  const OPENING_FIT_MAX_A_AU = 32;

  /** `descent-dojo --inject fit-pluto`: the fit's reach, widened. */
  let openingFitReachAu: number | null = null;

  /**
   * Whether the opening view is fit to hold this row.
   *
   * A fixed rule about the solar system, not about the registry's own
   * `availability.descend`: which rows carry ground is data that changes with
   * every bake, and the opening framing is not allowed to move with it. A
   * satellite is never in the fit - it orbits its primary and follows it into
   * frame.
   */
  function inOpeningFit(row: BodyRow): boolean {
    if (row.orbit === null || row.orbit.parent_relative) return false;
    return row.orbit.a_au <= (openingFitReachAu ?? OPENING_FIT_MAX_A_AU);
  }

  /**
   * What the camera can focus on.
   *
   * The camera asks the orrery rather than holding positions of its own, so a
   * focus point is always the propagator's answer at the clock's instant and
   * never a copy that could go stale by a frame.
   */
  const _shipCentre: WorldVec = { x: 0, y: 0, z: 0 };
  /**
   * The ship's own place this instant, scene axes.
   *
   * Read through the bus on every call rather than off the marker's last frame:
   * the camera is placed before the marker is updated, and a focus point a
   * frame behind the thing it is on is a camera that drifts while the clock is
   * warped.
   */
  function shipCentre(): WorldVec | null {
    const at = simApi.ship.at();
    if (at === null) return null;
    const w = eclipticToScene(at, 1);
    _shipCentre.x = w.x;
    _shipCentre.y = w.y;
    _shipCentre.z = w.z;
    return _shipCentre;
  }

  const focusBodies: FocusBodies = {
    // The ship answers here beside Sol and the registry's rows: the focus
    // camera addresses whatever it is pointed at by id, so a marker that is not
    // a body is reachable without a row and without a rung of its own.
    radiusM: (id) => (id === SHIP_MARKER_ID ? SHIP_MARKER_RADIUS_M
      : datumRadiusM(id) ?? (id === 'sol' ? 695700000 : null)),
    centre: (id) => (id === SHIP_MARKER_ID ? shipCentre()
      : id === 'sol' ? ORIGIN : deps.bodyScene.state.orrery?.byId(id)?.world ?? null),
    pole: (id) => {
      const b = deps.bodyScene.state.orrery?.byId(id);
      if (!b) return null;
      _v.set(0, 1, 0).applyQuaternion(b.quaternion);
      return { x: _v.x, y: _v.y, z: _v.z };
    },
    surface: (id, lat, lon) => {
      if (id === 'sol') {
        // Sol is drawn by its own billboarded disc and is in no `byId` map, but
        // it carries the same body-fixed rotation every globe does, so a point
        // on it is reachable the same way. No ground offset: there is no
        // terrain under a photosphere to hold the eye clear of.
        const q = deps.bodyScene.state.orrery?.sol.bodyQuaternion() ?? null;
        const r = datumRadiusM(id);
        if (q === null || r === null) return null;
        _v.copy(latLonToVec3(lat, lon, r)).applyQuaternion(q);
        return { x: ORIGIN.x + _v.x, y: ORIGIN.y + _v.y, z: ORIGIN.z + _v.z };
      }
      const b = deps.bodyScene.state.orrery?.byId(id);
      // On the terrain, not on the datum. The focus point is what altitude is
      // measured to, so a focus on the datum puts the camera inside Tharsis at
      // 3 km and 2.5 km over Jezero's ground at the same reading. The offset
      // eases in rather than snapping, because a leaf arriving must change
      // precision and not behaviour (02-continuous-descent.md, "The camera").
      return b
        ? surfaceWorld(b, lat, lon, (datumRadiusM(id) ?? b.row.radius_m) + deps.focusGround.groundAt(id, lat, lon))
        : null;
    },
    descendable: (id) => rowById(id)?.availability.descend ?? false,
    // The marker's floor is its own SYSTEM edge, so the wheel bottoms out at
    // the band it was picked in: there is no ladder under a ship, no
    // quadrangles, no tiles and no rung below the heliocentric one to reach.
    floorAltitudeM: (id) => (id === SHIP_MARKER_ID
      ? bandEdges(SHIP_MARKER_RADIUS_M).SYSTEM : descentFloorM(id)),
    groundRadiusM: (id, point) => deps.pyramidView.groundRadiusUnder(id, point),
    // Null while the walkaround's rig owns the camera, because that rig has its
    // own floor and this one must not move it. Otherwise the drawn ground, and
    // where none is drawn the datum, so a globe-map-only body still has a
    // bottom under the wheel.
    eyeFloorRadiusM: (id, point) => (plotCamera() || id === SHIP_MARKER_ID
      ? null : deps.pyramidView.groundRadiusUnder(id, point) ?? datumRadiusM(id)),
    // Inside a cloud-top corridor the camera looks along the limb, not down at
    // the deck: straight down on a class (c) body is a featureless globe and
    // the three strata are all above the eye. Above the corridor's own top the
    // body is an ordinary globe again and the altitude's ceiling is what binds.
    pitchCeilingFor: (id, altitudeM) => {
      const geo = corridorFor(id);
      return geo !== null && altitudeM <= geo.topAltitudeM ? CORRIDOR_PITCH_CEILING : null;
    },
    centreAnchorOnly: (id) => corridorFor(id) !== null,
    // A person's eye height over the drawn ground, in the one band whose label
    // promises a walkaround, and only on a body that will never build a
    // whole-file plot mesh - on Mars and Luna `plotDistanceFromAltitude`
    // floors the same thing on the plot's own rig at `SURFACE_EYE_FLOOR_M`. It
    // binds under about 12 m of altitude at the walkaround's own pitch, so no
    // band above SURFACE moves.
    //
    // Smooth ground with no site row, and not `plot === null` alone, for the
    // reason `enterStandingPose` records: the mesh lands a beat after the band
    // edge, and standing the eye on the pyramid in between dropped it from 152 m
    // to 1.7 m and flattened the aim to 1.3 degrees for as long as the build
    // took, which put the leaf's own metres-per-texel ground across the lower 44
    // per cent of the frame with no texture in it (`surface-clamp --band`). A
    // siteless body with relief builds its mesh from the leaves the same beat late,
    // but only a descent stepped across the edge waits for it: a `goTo` into the
    // band (LAND, a teleport) stands, or the eye floor lifts it out of SURFACE first.
    standingEyeFloorM: (id, altitudeM, steppedIn) => (
      !deps.injects.state.legacyStanding && deps.live.plot === null && !deps.siteRegistry.SITES.some((s) => s.body === id)
        && (smoothGround(id) || !steppedIn || deps.injects.state.legacySitelessStand)
        && id === ladderBody() && descendable()
        && bandAt(altitudeM, registryDatumM(id)) === 'SURFACE'
        ? STANDING_EYE_M : null),
    // Just over the body's own horizon, so the limb sits low in the frame with
    // the three strata stacked above it. The dip is the altitude's own, so the
    // framing holds across the corridor's decade instead of at one band.
    lookLiftRad: (id, altitudeM) => {
      const geo = corridorFor(id);
      if (geo === null || altitudeM > geo.topAltitudeM) return null;
      const r = datumRadiusM(id) ?? registryDatumM(id);
      if (!(r > 0)) return null;
      const dip = Math.acos(Math.min(1, r / (r + Math.max(1, altitudeM))));
      return CORRIDOR_HORIZON_MARGIN - dip;
    },
    framingTargets: (scope) => {
      const out: WorldVec[] = [ORIGIN];
      for (const b of deps.bodyScene.state.orrery?.bodies ?? []) {
        if (scope === 'opening') {
          if (!inOpeningFit(b.row)) continue;
        } else if (b.row.pick_band !== 'SYSTEM') continue;
        out.push(b.world);
      }
      return out;
    },
  };

  const focusCam = createFocusCamera({
    camera,
    bodies: focusBodies,
    // Only reachable before the registry lands, where there is no body to
    // frame; the load handler reframes the moment there is a row.
    fallbackRadiusM: () => registryDatumM(ladderBody()),
    groundFloorOff: () => zoomFloorOff(),
    standEaseOff: () => deps.injects.state.STAND_EASE_OFF,
  });
  own(() => focusCam.dispose());

  /**
   * The pose the opening fit last left, so a re-fit can tell whether the view
   * is still its own.
   *
   * The heliocentric fit is a function of the aspect and of where the bodies
   * are, so a resize and the registry landing both re-choose it - and it is the
   * WHOLE-system framing, which on the Atlas's default body is the one thing a
   * player zooming in on Sol is trying to leave. Seeded from the camera's
   * constructed pose, which is that same framing before anything has framed.
   */
  let openingPose = { ...focusCam.state };

  /**
   * `zoom-hold-check --inject refit-over-zoom`: re-fit whatever the pose is,
   * which is the state that threw a wheel spent on Sol back out to the system.
   */
  const refitOverZoomInject = typeof location !== 'undefined' && TEST_SWITCHES
    && new URLSearchParams(location.search).get('atlasrefitoverzoom') === '1';

  /** Frame the opening view and remember the pose it left. */
  function fitOpening(aspectRatio: number, scope?: FramingScope): void {
    focusCam.fitAll(aspectRatio, scope, deps.injects.state.FIT_KEEPS_PITCH);
    openingPose = { ...focusCam.state };
  }

  /** Whether the camera is still exactly where the opening fit put it. */
  function openingFitHeld(): boolean {
    if (refitOverZoomInject) return true;
    const s = focusCam.state;
    return s.bodyId === openingPose.bodyId && s.altitudeM === openingPose.altitudeM
      && s.yaw === openingPose.yaw && s.pitch === openingPose.pitch;
  }

  /** Altitude is the distance to the focus point, which is what the bands read. */
  /**
   * Whether the camera is on the ship rather than on a body or the whole system.
   *
   * Read off the focus camera and never held as a flag of its own: every path
   * that points the camera somewhere else writes that id, so there is no state
   * left saying the view is on the ship after it has been taken off it.
   */
  const shipFocused = (): boolean => focusCam.state.bodyId === SHIP_MARKER_ID;
  const altitude = (): number => focusCam.state.altitudeM;
  const band = (): Band => focusCam.band();
  /** The ladder rung the current altitude reads as. */
  const tierOfAltitude = (): Tier => focusCam.tier() as Tier;

  return {
    STANDING_EYE_M,
    OPENING_FIT_MAX_A_AU,
    inOpeningFit,
    focusBodies,
    focusCam,
    fitOpening,
    openingFitHeld,
    shipFocused,
    altitude,
    band,
    tierOfAltitude,
    state: {
      get openingFitReachAu() { return openingFitReachAu; },
      set openingFitReachAu(v) { openingFitReachAu = v; },
    },
  };
}
